import { PageHero } from '@/components/PageHero';
import { CtaBanner } from '@/components/CtaBanner';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { AREAS } from '@/data/site';
import { AREAS_EN } from '@/data/en/site';
import { STATIC_IT } from '@/data/staticIt';
import { STATIC_EN } from '@/data/staticEn';
import { useLang, LangLink } from '@/i18n/LangContext';
import { ArrowRight, HeartPulse, Home as HomeIcon, Building2, PiggyBank } from 'lucide-react';

const ICONS = { HeartPulse, Home: HomeIcon, Building2, PiggyBank };

const EditorialCopy = ({ content, dark = false }) => {
  if (!content) return null;
  return (
    <section className={dark ? 'bg-brand-deep text-white' : 'bg-background'}>
      <div className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className={`text-xs uppercase tracking-[0.25em] font-semibold mb-4 ${dark ? 'text-brand-light' : 'text-primary'}`}>{content.eyebrow}</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-balance">{content.title}</h2>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-7 max-w-3xl">
          <div className={`space-y-5 text-base md:text-lg leading-relaxed ${dark ? 'text-white/75' : 'text-foreground/72'}`}>
            {content.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
};


export default function Soluzioni() {
  const { lang, t } = useLang();
  const c = lang === 'en' ? STATIC_EN.soluzioni : STATIC_IT.soluzioni;
  const areas = lang === 'en' ? AREAS_EN : AREAS;
  useSeo(c.seo);

  return (
    <div data-testid="soluzioni-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        summary={c.summary}
        image="/assets/home-hero.webp"
        imageAlt={c.title}
        breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: c.eyebrow }]}
      />
      <EditorialCopy content={c.introContent} />
      <div className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
          {areas.map((area, i) => {
            const Icon = ICONS[area.icon];
            return (
              <Reveal key={area.key} delay={i * 0.05}>
                <LangLink
                  to={area.path}
                  data-testid={`soluzioni-area-${area.key}`}
                  className="group flex flex-col bg-white hover:bg-brand-pale/40 transition-colors duration-300 h-full"
                >
                  <div className="relative h-44 md:h-52 overflow-hidden">
                    <img src={area.image} alt={area.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/75 via-brand-deep/10 to-transparent" />
                    <span className="absolute top-5 left-6 font-heading text-sm font-bold text-white/90 tracking-widest">{String(i + 1).padStart(2, '0')}</span>
                    <div className="absolute bottom-5 left-6 flex items-center gap-3">
                      <span className="bg-brand-pale p-2.5 rounded-lg">
                        <Icon className="h-6 w-6 text-primary" />
                      </span>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">{area.name}</h2>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <p className="text-base md:text-lg leading-relaxed text-foreground/70 max-w-2xl">{area.short}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary mt-6">
                      {t('explore')}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                </LangLink>
              </Reveal>
            );
          })}
        </div>
      </div>
      <EditorialCopy content={c.approachContent} dark />
      <div className="mx-auto max-w-site px-5 md:px-8">
        <CtaBanner
          title={c.cta.title}
          text={c.cta.text}
          label={c.cta.label}
        />
      </div>
    </div>
  );
}
